import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, Button, Divider, Menu, MenuItem, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useModals } from "../../../../../context/ModalContext";
import { BaseModalComponent } from "../../../../common/BaseModalComponent";
import { InvoiceCreateView } from "../../../../invoice/create/InvoiceCreateView";
import { colors, alphaVar } from "../../../../ui/colors";
import type { JobEventsController } from "../hooks/useJobEventController";
import type { JobEventsPaginatedQuery } from "../hooks/useJobEventsPaginated";
import { sortJobEventsByStartDate, useJobEventsBoard } from "./context";
import { JobEventFilters } from "./filters/JobEventFilters";
import { JobEventsList } from "./list/JobEventsList";
import { BulkEventWorkforceView } from "./list/BulkEventWorkforceView";
import { KeyboardArrowDown } from "@mui/icons-material";

/** Matches JobEventRow "Invoice" trigger (orange pill). */
const invoiceRowStyleButtonSx = {
  height: 32,
  borderRadius: "8px",
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "none" as const,
  color: colors.orange[600],
  bgcolor: alphaVar(colors.orange[100], 0.5),
  px: 1.5,
  transition: "all 0.15s ease",
  "&:hover": { bgcolor: colors.orange[100], color: colors.orange[700] },
  "&:active": { transform: "scale(0.97)" },
};

interface Props {
  eventsQuery: JobEventsPaginatedQuery;
  controller: JobEventsController;
}

export function JobEventsBoard({ controller, eventsQuery }: Props) {
  const { openModal } = useModals();
  const {
    isCreateInvoiceForMultipleVisitsOpen,
    setIsCreateInvoiceForMultipleVisitsOpen,
    jobEvents,
    setJobEvents,

    isBulkWorkforceOpen,
    setIsBulkWorkforceOpen,
    bulkWorkforceEvents,
    setBulkWorkforceEvents,
  } = useJobEventsBoard();

  const job = controller.job;
  const multiInvoiceModalKey = `Dashboard-InvoiceCreate-Multi-${job.id}`;
  const bulkWorkforceModalKey = `Dashboard-BulkWorkforce-${job.id}`;
  const bulkWorkforceByDateModalKey = `Dashboard-BulkWorkforce-Date-${job.id}`;
  const visitCount = eventsQuery.totalItems ?? 0;
  const canOfferMultiVisitInvoice = visitCount >= 2;

  const [workforceAnchorEl, setWorkforceAnchorEl] = useState(null);

  useEffect(() => {
    // Avoid clearing selection while a new page/filter request is in flight (totalItems can be 0 briefly without keepPreviousData).
    if (eventsQuery.isFetching) return;
    if (!canOfferMultiVisitInvoice && isCreateInvoiceForMultipleVisitsOpen) {
      setIsCreateInvoiceForMultipleVisitsOpen(false);
      setJobEvents([]);
    }
  }, [
    canOfferMultiVisitInvoice,
    isCreateInvoiceForMultipleVisitsOpen,
    eventsQuery.isFetching,
    setIsCreateInvoiceForMultipleVisitsOpen,
    setJobEvents,
  ]);

  useEffect(() => {
    if (eventsQuery.isFetching) return;

    if (visitCount === 0 && isBulkWorkforceOpen) {
      setIsBulkWorkforceOpen(false);
      setBulkWorkforceEvents([]);
    }
  }, [visitCount, isBulkWorkforceOpen, eventsQuery.isFetching, setIsBulkWorkforceOpen, setBulkWorkforceEvents]);

  const exitMultipleVisitsMode = () => {
    setIsCreateInvoiceForMultipleVisitsOpen(false);
    setJobEvents([]);
  };

  const exitBulkWorkforceMode = () => {
    setIsBulkWorkforceOpen(false);
    setBulkWorkforceEvents([]);
  };

  const handleContinueMultipleInvoice = () => {
    if (jobEvents.length === 0) return;
    const scopeOfWorkUid = job.scopeOfWorkUid;
    if (!scopeOfWorkUid) return;

    const snapshot = sortJobEventsByStartDate(jobEvents.map((e) => ({ ...e })));

    const jobLabel = job.alias ? `Job #${job.id} — ${job.alias}` : `Job #${job.id}`;
    const visitGroups = [
      {
        jobId: job.id,
        jobLabel,
        eventUids: snapshot.map((e) => e.uid).filter((uid): uid is string => Boolean(uid)),
      },
    ];

    openModal(
      { key: multiInvoiceModalKey, preventParentTrigger: true },
      <InvoiceCreateView
        jobUid={job.id}
        jobEvents={snapshot}
        clientUid={job.clientUid}
        scopeOfWorkUid={scopeOfWorkUid}
        eventSchedulerUid={job.eventUid}
        invoiceModal={multiInvoiceModalKey}
        visitGroups={visitGroups}
      />,
      null,
    );

    exitMultipleVisitsMode();
  };

  const handleContinueBulkWorkforce = () => {
    if (bulkWorkforceEvents.length === 0) return;

    const snapshot = sortJobEventsByStartDate(bulkWorkforceEvents.map((event) => ({ ...event })));

    openModal(
      { key: bulkWorkforceModalKey, preventParentTrigger: true },
      <BulkEventWorkforceView
        job={job}
        jobEvents={snapshot}
        modalKey={bulkWorkforceModalKey}
        onDone={() => {
          eventsQuery.refetch?.();
        }}
      />,
      null,
    );

    exitBulkWorkforceMode();
  };

  const handleOpenBulkWorkforceByDate = () => {
    setIsBulkWorkforceOpen(false);
    setBulkWorkforceEvents([]);

    openModal(
      { key: bulkWorkforceByDateModalKey, preventParentTrigger: true },
      <BulkEventWorkforceView
        key={bulkWorkforceByDateModalKey}
        job={job}
        jobEvents={[]}
        modalKey={bulkWorkforceByDateModalKey}
        initialApplyFromDate
        onDone={() => {
          eventsQuery.refetch?.();
        }}
      />,
      null,
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "row", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <JobEventFilters resetPage={eventsQuery.resetPage} controller={controller} />

        {visitCount > 0 && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ ml: { xs: 0, sm: "auto" }, width: { xs: "100%", sm: "auto" }, justifyContent: "flex-end" }}
          >
            {!isCreateInvoiceForMultipleVisitsOpen && !isBulkWorkforceOpen ? (
              <>
                {canOfferMultiVisitInvoice && (
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<AddRoundedIcon sx={{ fontSize: "15px !important" }} />}
                    onClick={() => setIsCreateInvoiceForMultipleVisitsOpen(true)}
                    sx={invoiceRowStyleButtonSx}
                  >
                    Invoice multiple visits
                  </Button>
                )}

                <Button
                  variant="text"
                  size="small"
                  startIcon={<AddRoundedIcon sx={{ fontSize: "15px !important" }} />}
                  endIcon={<KeyboardArrowDown sx={{ fontSize: 18 }} />}
                  onClick={(e) => setWorkforceAnchorEl(e.currentTarget)}
                  sx={{
                    ...invoiceRowStyleButtonSx,
                    color: colors.blue[700],
                    bgcolor: alphaVar(colors.blue[100], 0.5),
                    "&:hover": { bgcolor: colors.blue[100], color: colors.blue[800] },
                  }}
                >
                  Edit workforce
                </Button>

                <Menu
                  anchorEl={workforceAnchorEl}
                  open={Boolean(workforceAnchorEl)}
                  onClose={() => setWorkforceAnchorEl(null)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setWorkforceAnchorEl(null);
                      setIsBulkWorkforceOpen(true);
                    }}
                  >
                    Edit selected visits
                  </MenuItem>

                  <MenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setWorkforceAnchorEl(null);
                      handleOpenBulkWorkforceByDate();
                    }}
                  >
                    Edit workforce by start date
                  </MenuItem>
                </Menu>
              </>
            ) : isCreateInvoiceForMultipleVisitsOpen ? (
              <>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<CloseRoundedIcon sx={{ fontSize: "14px !important" }} />}
                  onClick={exitMultipleVisitsMode}
                  sx={{
                    height: 32,
                    borderRadius: "8px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textTransform: "none",
                    color: colors.gray[400],
                    bgcolor: alphaVar(colors.gray[100], 0.5),
                    px: 1.5,
                    transition: "all 0.15s ease",
                    "&:hover": { bgcolor: colors.gray[100], color: colors.gray[500] },
                    "&:active": { transform: "scale(0.97)" },
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="text"
                  size="small"
                  startIcon={<CheckRoundedIcon sx={{ fontSize: "15px !important" }} />}
                  disabled={jobEvents.length === 0}
                  onClick={handleContinueMultipleInvoice}
                  sx={{
                    ...invoiceRowStyleButtonSx,
                    "&:disabled": {
                      color: alphaVar(colors.orange[600], 0.45),
                      bgcolor: alphaVar(colors.orange[100], 0.35),
                    },
                  }}
                >
                  Continue ({jobEvents.length})
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<CloseRoundedIcon sx={{ fontSize: "14px !important" }} />}
                  onClick={exitBulkWorkforceMode}
                  sx={{
                    height: 32,
                    borderRadius: "8px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textTransform: "none",
                    color: colors.gray[400],
                    bgcolor: alphaVar(colors.gray[100], 0.5),
                    px: 1.5,
                    transition: "all 0.15s ease",
                    "&:hover": { bgcolor: colors.gray[100], color: colors.gray[500] },
                    "&:active": { transform: "scale(0.97)" },
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="text"
                  size="small"
                  startIcon={<CheckRoundedIcon sx={{ fontSize: "15px !important" }} />}
                  disabled={bulkWorkforceEvents.length === 0}
                  onClick={handleContinueBulkWorkforce}
                  sx={{
                    ...invoiceRowStyleButtonSx,
                    color: colors.blue[700],
                    bgcolor: alphaVar(colors.blue[100], 0.5),
                    "&:hover": { bgcolor: colors.blue[100], color: colors.blue[800] },
                    "&:disabled": {
                      color: alphaVar(colors.blue[700], 0.4),
                      bgcolor: alphaVar(colors.blue[100], 0.32),
                    },
                  }}
                >
                  Continue ({bulkWorkforceEvents.length})
                </Button>
              </>
            )}
          </Stack>
        )}
      </Box>

      <BaseModalComponent
        modalKey={multiInvoiceModalKey}
        modalLabel="Create invoice for multiple visits"
        defaultSizes={{ width: "80%", height: "85%" }}
        preventParentTrigger
      >
        {null}
      </BaseModalComponent>
      <BaseModalComponent
        modalKey={bulkWorkforceModalKey}
        modalLabel="Edit workforce for selected visits"
        defaultSizes={{ width: "80%", height: "85%" }}
        preventParentTrigger
      >
        {null}
      </BaseModalComponent>
      <BaseModalComponent
        modalKey={bulkWorkforceByDateModalKey}
        modalLabel="Edit workforce by date"
        defaultSizes={{ width: "80%", height: "85%" }}
        preventParentTrigger
      >
        {null}
      </BaseModalComponent>
      <Divider />

      <JobEventsList job={controller.job} eventsQuery={eventsQuery} />
    </Box>
  );
}
